import type {
	Interactions,
	InteractionReactions,
	Transformations,
	Transitions,
	Styles,
	Effects,
	VariantId,
	ReactionTypes,
	ToggleParams,
	TriggerType,
	Effect,
	modelData,
	InteractionsConfig,
	EffectId,
	ResolvedVariantRelation,
	AnimationOptions,
	InteractionEffects,
	RangeOffset,
} from '@wix/thunderbolt-becky-types'

const STATE_METHODS: { [key in ReactionTypes]?: ToggleParams['method'] } = {
	ToggleState: 'toggle',
	AddState: 'add',
	RemoveState: 'remove',
	RemoveAllStates: 'clear',
}

const KEBAB_TO_CAMEL_MAP: { [key: string]: TriggerType } = {
	click: 'click',
	hover: 'hover',
	'viewport-enter': 'viewEnter',
	'view-progress': 'viewProgress',
	'animation-end': 'animationEnd',
	'pointer-move': 'pointerMove',
	'page-visible': 'pageVisible',
}

const ANIMATION_PROPERTIES_MAP: { [key: string]: string } = {
	startOffset: 'rangeStart',
	endOffset: 'rangeEnd',
}

const omitProperties = (props: Array<string>, obj: Record<string, any>) => {
	return Object.keys(obj).reduce((acc, key) => {
		if (!props.includes(key)) {
			if (typeof obj[key] === 'object' && obj[key] !== null) {
				acc[key] = omitProperties(['id', 'metaData'], obj[key])
			} else {
				acc[key] = obj[key]
			}
		}
		return acc
	}, {} as Record<string, any>)
}

const animationOptionsFixer = (effectOptions: AnimationOptions) => {
	return Object.entries(effectOptions).reduce((acc: Record<string, any>, [key, value]) => {
		if (key === 'startOffset' || key === 'endOffset') {
			const offset = value as RangeOffset
			acc[ANIMATION_PROPERTIES_MAP[key]] = {
				name: offset?.name,
				offset: offset?.offset?.value,
				type: offset?.offset?.type,
			}
		} else {
			acc[key] = value
		}
		return acc
	}, {})
}

const effectsFixer = (
	effectIdToRefArray: Record<EffectId, Array<ResolvedVariantRelation<AnimationOptions> | AnimationOptions>>
) => {
	return (Object.keys(effectIdToRefArray) as Array<EffectId>).reduce((acc: InteractionEffects, key) => {
		const variantRelation = effectIdToRefArray[key].find((element) => element.type === 'VariantRelation') as
			| ResolvedVariantRelation<AnimationOptions>
			| undefined

		acc[key] = animationOptionsFixer(
			(variantRelation?.to
				? omitProperties(['id', 'type', 'metaData'], variantRelation.to)
				: (omitProperties(
						['id', 'type', 'metaData'],
						effectIdToRefArray[key]?.[0]
				  ) as AnimationOptions)) as AnimationOptions
		)

		return acc
	}, {})
}

const collectReactions = (reactionObjects: Array<InteractionReactions>) => {
	return reactionObjects.reduce((acc, reactionObject) => {
		const [variantId] = Object.keys(reactionObject) as Array<VariantId>
		const reactions = reactionObject[variantId]
		if (!acc[variantId]) {
			acc[variantId] = []
		}
		acc[variantId] = acc[variantId].concat(reactions)
		return acc
	}, {})
}

const getState = (
	stateVariantId: VariantId,
	transformations: Transformations,
	transitions: Transitions,
	styles: Styles
) => {
	return {
		transformation: transformations[stateVariantId],
		transition: { styleProperty: styles[stateVariantId], ...transitions[stateVariantId] },
	}
}

const getEffects = (variantRelationId: VariantId, data: modelData): Effects => {
	const effects: Array<Effect> = []
	const variantRelationReactions = data.reactions[variantRelationId]

	if (!variantRelationReactions) {
		return []
	}

	variantRelationReactions.forEach((reaction) => {
		effects.push({
			target: reaction.target,
			type: reaction.type,
			...(reaction.state && getState(reaction.state, data.transformations, data.transitions, data.styles)),
			...(reaction.effect && data.effects[reaction.effect]),
		})
	})

	return effects
}

const buildInteractions = (data: modelData): Interactions => {
	if (!data.interactionTriggers) {
		return []
	}

	const interactions: Interactions = []

	data.interactionTriggers.forEach((interactionTrigger) => {
		if (!KEBAB_TO_CAMEL_MAP.hasOwnProperty(interactionTrigger.trigger)) {
			return
		}

		const interactionEffects = getEffects(interactionTrigger.id, data)

		if (interactionTrigger.params) {
			const params = interactionTrigger.params.hasOwnProperty('threshold')
				? { ...interactionTrigger.params, type: 'once' as const }
				: interactionTrigger.params

			interactions.push({
				source: interactionTrigger.componentId,
				trigger: KEBAB_TO_CAMEL_MAP[interactionTrigger.trigger],
				params,
				effects: interactionEffects,
			})
		} else if (interactionTrigger.trigger === 'view-progress') {
			interactions.push({
				source: interactionTrigger.componentId,
				trigger: KEBAB_TO_CAMEL_MAP[interactionTrigger.trigger],
				effects: interactionEffects,
			})
		} else {
			const stateEffectsMap = interactionEffects.reduce((acc, currEffect) => {
				if (!acc[currEffect.type]) {
					acc[currEffect.type] = []
				}
				acc[currEffect.type].push(currEffect)
				return acc
			}, {} as { [key: string]: Effects })

			Object.entries(STATE_METHODS).forEach(([stateType, method]) => {
				if (stateEffectsMap[stateType] && stateEffectsMap[stateType].length > 0) {
					interactions.push({
						source: interactionTrigger.componentId,
						trigger: KEBAB_TO_CAMEL_MAP[interactionTrigger.trigger],
						params: { method },
						effects: stateEffectsMap[stateType],
					})
				}
			})
		}
	})

	return interactions
}

const buildConfig = (data: { interactions: Interactions }): InteractionsConfig => {
	return {
		effects: {},
		conditions: {},
		interactions: data.interactions,
	}
}

export { buildInteractions, buildConfig, collectReactions, effectsFixer }
